import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Chip, List, ListItem, ListItemIcon, ListItemText, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { estaNoElectron, type NivelKiosk } from '@/lib/balanca'
import ScreenLockPortraitIcon from '@mui/icons-material/ScreenLockPortrait'
import LaunchIcon from '@mui/icons-material/Launch'

const RELEITURA_MS = 2_000

export function PreferenciasAparelhoTotem(): JSX.Element | null {
    const [kioskAtivo, setKioskAtivo] = useState<boolean | null>(null)
    const [inicioAutomatico, setInicioAutomatico] = useState<boolean | null>(null)
    const [nivel, setNivel] = useState<NivelKiosk | null>(null)
    const [confirmandoKiosk, setConfirmandoKiosk] = useState(false)
    const [erro, setErro] = useState<string | null>(null)
    const esperandoTelaInicial = kioskAtivo === true && !!nivel && !nivel.deviceOwner && !nivel.telaInicial

    const lerNivel = useCallback(async (): Promise<void> => {
        try {
            const n = await window.pdvDesktop?.nivelKiosk?.()
            if (n) setNivel(n)
        } catch { }
    }, [])

    useEffect(() => {
        if (!estaNoElectron()) return

        let vivo = true

        void (async () => {
            try {
                const n = await window.pdvDesktop?.nivelKiosk?.()
                if (vivo && n) setNivel(n)

                const kiosk = await window.pdvDesktop?.isKioskMode?.()
                if (vivo && typeof kiosk === 'boolean') setKioskAtivo(kiosk)

                const prefs = await window.pdvDesktop?.obterPreferenciasTotem?.()
                if (vivo && prefs) setInicioAutomatico(prefs.inicioAutomatico)
            } catch { }
        })()

        return () => { vivo = false }
    }, [])

    async function alternarKiosk(): Promise<void> {
        const novo = !kioskAtivo

        setConfirmandoKiosk(false)
        setErro(null)

        const ok = await window.pdvDesktop?.setKioskMode?.(novo)

        if (ok === false) {
            setErro('Não foi possível salvar neste aparelho. Tente de novo.')
            return
        }

        setKioskAtivo(novo)
        void lerNivel()
    }

    async function alterarInicioAutomatico(ativo: boolean): Promise<void> {
        setInicioAutomatico(ativo)
        setErro(null)

        const salvo = await window.pdvDesktop?.definirInicioAutomatico?.(ativo)
        if (typeof salvo !== 'boolean') return

        setInicioAutomatico(salvo)
        if (salvo !== ativo) setErro('Não foi possível salvar neste aparelho. Tente de novo.')
    }

    useEffect(() => {
        if (!esperandoTelaInicial) return

        const timer = window.setInterval(() => void lerNivel(), RELEITURA_MS)
        return () => window.clearInterval(timer)
    }, [esperandoTelaInicial, lerNivel])

    if (!estaNoElectron()) return null
    if (kioskAtivo === null && inicioAutomatico === null) return null

    return (
        <Stack spacing={1}>
            {erro && <Alert severity="error">{erro}</Alert>}

            {kioskAtivo !== null && (
                confirmandoKiosk ? (
                    <Alert
                        severity="warning"
                        action={
                            <Button size="small" color="inherit" onClick={() => void alternarKiosk()}>
                                Continuar
                            </Button>
                        }
                    >
                        {textoConfirmacao(kioskAtivo, nivel)}
                    </Alert>
                ) : (
                    <Stack spacing={1} sx={{ pt: 2 }}>
                        <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center' }}>
                            <List sx={{ width: '100%', p: 0 }}>
                                <ListItem sx={{ pl: 1, py: 0 }}>
                                    <ListItemIcon sx={{ minWidth: '48px' }}>
                                        <ScreenLockPortraitIcon sx={{ fontSize: '30px' }} />
                                    </ListItemIcon>

                                    <ListItemText
                                        primary="Modo bloqueio (kiosk)"
                                        secondary="Quando ativo, o totem abre em tela cheia e o cliente não consegue sair dele."
                                        slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                    />
                                </ListItem>
                            </List>

                            <Chip label={kioskAtivo ? 'Ativo' : 'Inativo'} color={kioskAtivo ? 'primary' : 'warning'} size={'small'} />
                        </Stack>

                        <Stack>
                            <Button
                                fullWidth
                                variant="contained"
                                color={kioskAtivo ? 'error' : 'success'}
                                sx={{ textTransform: 'none' }}
                                onClick={() => setConfirmandoKiosk(true)}
                            >
                                {kioskAtivo ? 'Sair do modo kiosk' : 'Entrar em modo kiosk'}
                            </Button>
                        </Stack>
                    </Stack>
                )
            )}

            {kioskAtivo && nivel && !confirmandoKiosk && (
                <StatusTrava
                    nivel={nivel}
                    onTornarTelaInicial={() => void window.pdvDesktop?.pedirTelaInicial?.()}
                />
            )}

            {inicioAutomatico !== null && (
                <Stack sx={{ pt: 2 }}>
                    <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center' }}>
                        <List sx={{ width: '100%', p: 0 }}>
                            <ListItem sx={{ pl: 1, py: 0 }}>
                                <ListItemIcon sx={{ minWidth: '48px' }}>
                                    <LaunchIcon sx={{ fontSize: '30px' }} />
                                </ListItemIcon>

                                <ListItemText
                                    primary="Inicialização automática"
                                    secondary="Quando ativo, o totem do Sischef abre sozinho quando o aparelho liga."
                                    slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                />
                            </ListItem>
                        </List>
                    </Stack>

                    <TextField
                        select
                        fullWidth
                        label="Abrir o totem sozinho quando o dispositivo ligar"
                        value={inicioAutomatico ? 'SIM' : 'NAO'}
                        onChange={(e) => void alterarInicioAutomatico(e.target.value === 'SIM')}
                        sx={{ mt: 2 }}
                    >
                        <MenuItem value="SIM">Sim</MenuItem>
                        <MenuItem value="NAO">Não</MenuItem>
                    </TextField>
                </Stack>
            )}
        </Stack>
    )
}

function textoConfirmacao(kioskAtivo: boolean, nivel: NivelKiosk | null): string {
    if (!nivel) {
        return kioskAtivo
            ? 'O totem vai reiniciar e a tela fica liberada nas próximas vezes que ligar.'
            : 'O totem vai reiniciar e passa a abrir em tela cheia travada nas próximas vezes que ligar.'
    }

    if (kioskAtivo) return 'O totem destrava, e o botão de início volta a abrir a tela do Android.'

    return nivel.deviceOwner
        ? 'O totem trava: os botões do Android somem e só dá para sair por aqui.'
        : 'O totem vira a tela inicial deste aparelho. O Android vai pedir para confirmar, por favor aceite.'
}


function StatusTrava({ nivel, onTornarTelaInicial }: {
    nivel: NivelKiosk
    onTornarTelaInicial: () => void
}): JSX.Element {
    if (nivel.deviceOwner) {
        return (
            <Alert severity="success">
                Trava total: os botões do Android estão escondidos e o cliente não consegue sair do totem.
            </Alert>
        )
    }

    if (nivel.telaInicial) {
        return (
            <Alert severity="info">
                <strong>Atualmente o totem está em modo de trava básica:</strong> o botão de início e o reinício do aparelho podem ser usados. <br /> <br /> <strong>Observação:</strong> caso tenha necessidade de esconder os botões do Android
                contate o suporte.
            </Alert>
        )
    }

    return (
        <Stack spacing={1}>
            <Alert severity="warning">
                O totem ainda não é a tela inicial: o botão de início leva o cliente para fora dele.
            </Alert>

            <Button fullWidth variant="contained" sx={{ textTransform: 'none' }} onClick={onTornarTelaInicial}>
                Tornar o totem a tela inicial
            </Button>
        </Stack>
    )
}
